import React, { createContext, useContext, useEffect, useState } from 'react';
import { AuthUser, authService } from '../services/authService';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const handleAuthError = (message: string) => {
    setError(message);
  };

  const clearError = () => setError(null);

  const login = async (email: string, password: string) => {
    try {
      clearError();
      const loggedInUser = await authService.login(email, password);
      setUser(loggedInUser);
    } catch (err) {
      handleAuthError(err instanceof Error ? err.message : 'Unable to login');
      throw err;
    }
  };

  const register = async (email: string, password: string): Promise<AuthUser> => {
    try {
      clearError();
      const newUser = await authService.register(email, password);
      // Don't set user in context - they need to verify email first
      // Return the user object so the UI can show a success message
      return newUser;
    } catch (err) {
      handleAuthError(err instanceof Error ? err.message : 'Unable to register');
      throw err;
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const refreshed = await authService.getCurrentUser();
      setUser(refreshed);
    } catch {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, error, login, register, logout, refreshUser, clearError }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

